import React, { useState } from 'react';
import {
    Anchor,
    Box,
    Button,
    Form,
    FormField,
    Image,
    Main,
    Page,
    PageContent,
    ResponsiveContext,
    Text,
    TextInput,
} from 'grommet';
import { MailOption } from 'grommet-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../../api';
import { Utils } from '../../helpers/Utils';
import PropTypes from 'prop-types';

const validateEmailField = (fieldValue) => {
    if (Utils.validateEmail(fieldValue)) return undefined;

    return { message: 'Enter a valid email address', status: 'error' };
};

const RESET_SENT_MESSAGE =
    'If an account exists for this email, password reset instructions have been sent.';

export const ForgotPassword = ({
    slugParam,
    loginPathTemplate,
    resetPathTemplate,
    marketingImage,
    bannerLogo,
}) => {
    const params = useParams();
    const slug = slugParam ? params[slugParam] : null;
    const loginPath = slug
        ? (loginPathTemplate || '/login').replace(':slug', slug)
        : loginPathTemplate || '/login';
    const resetPath = slug
        ? (resetPathTemplate || '/reset-password').replace(':slug', slug)
        : resetPathTemplate || '/reset-password';
    const resetPasswordUrl = `${window.location.origin}${resetPath}`;
    const size = React.useContext(ResponsiveContext);
    const isMobile = size === 'small';
    const [value, setValue] = useState({ email: '' });
    const [buttonBusy, setButtonBusy] = useState(false);
    const [resetSent, setResetSent] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();

    const handleSubmit = async () => {
        setButtonBusy(true);
        setErrorMessage('');
        try {
            await API.post('/api/auth/forgot-password', {
                email: value.email,
                resetPasswordUrl,
            });
            setResetSent(true);
        } catch (error) {
            console.log(error);
            setErrorMessage(
                'There was an issue sending the reset email. Please try again.',
            );
        }
        setButtonBusy(false);
    };

    if (!marketingImage || !bannerLogo) {
        return (
            <Page>
                <PageContent>
                    <Box width="medium">
                        {resetSent ? (
                            <Box gap="small">
                                <Text>{RESET_SENT_MESSAGE}</Text>
                                <Button
                                    label="Back to login"
                                    onClick={() => navigate(loginPath)}
                                />
                            </Box>
                        ) : (
                            <Form
                                value={value}
                                onChange={(nextValue) => setValue(nextValue)}
                                onSubmit={() => handleSubmit()}>
                                <Box gap="small">
                                    <FormField
                                        name="email"
                                        htmlFor="forgot-email-input-id"
                                        label="Email"
                                        validate={validateEmailField}
                                        error={errorMessage}>
                                        <TextInput
                                            name="email"
                                            id="forgot-email-input-id"
                                            icon={<MailOption />}
                                        />
                                    </FormField>
                                    <Button
                                        type="submit"
                                        primary
                                        label="Send reset link"
                                        busy={buttonBusy}
                                    />
                                    <Button
                                        label="Back to login"
                                        onClick={() => navigate(loginPath)}
                                    />
                                </Box>
                            </Form>
                        )}
                    </Box>
                </PageContent>
            </Page>
        );
    }

    return (
        <Main>
            <Page kind="full" height="100vh" background="enrich-grey">
                <PageContent pad="none" height="100%" justify="center">
                    <Box direction="row">
                        {!isMobile && (
                            <Box>
                                <Image
                                    src={marketingImage}
                                    height="100%"
                                    fit="cover"
                                />
                            </Box>
                        )}
                        <Box
                            background="white"
                            round="15px"
                            width="100%"
                            pad="large"
                            margin="medium"
                            align="stretch"
                            justify="evenly"
                            gap="small">
                            <Image
                                src={bannerLogo}
                                width="200px"
                                alignSelf="center"
                            />
                            {resetSent ? (
                                <Box gap="small" align="center">
                                    <Text textAlign="center">
                                        {RESET_SENT_MESSAGE}
                                    </Text>
                                    <Anchor
                                        label="Back to login"
                                        onClick={() => navigate(loginPath)}
                                    />
                                </Box>
                            ) : (
                                <Form
                                    value={value}
                                    onChange={(nextValue) =>
                                        setValue(nextValue)
                                    }
                                    onSubmit={handleSubmit}>
                                    <Box gap="10px">
                                        <FormField
                                            name="email"
                                            htmlFor="forgot-email-input-id"
                                            label="Email"
                                            validate={validateEmailField}
                                            error={errorMessage}>
                                            <TextInput
                                                name="email"
                                                id="forgot-email-input-id"
                                                icon={<MailOption />}
                                            />
                                        </FormField>
                                        <Button
                                            type="submit"
                                            primary
                                            fill="horizontal"
                                            label="Send reset link"
                                            busy={buttonBusy}
                                        />
                                        <Anchor
                                            label="Back to login"
                                            alignSelf="center"
                                            onClick={() => navigate(loginPath)}
                                        />
                                    </Box>
                                </Form>
                            )}
                        </Box>
                    </Box>
                </PageContent>
            </Page>
        </Main>
    );
};

ForgotPassword.propTypes = {
    slugParam: PropTypes.string,
    loginPathTemplate: PropTypes.string,
    resetPathTemplate: PropTypes.string,
    marketingImage: PropTypes.string,
    bannerLogo: PropTypes.string,
};
